import React, { useContext } from 'react';

import styled from 'styled-components';
import Input from '@components/common/form/new/Input';
import { GENDER_OPTIONS } from '@constants/enrollmentList/enrollmentList.constant';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { ValidationErrorMessage } from '@components/common/form';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { usePermissions } from '@hooks/usePermissions';
import Dropdown from '@lib/inputs/Dropdown';
import ReactDatepicker from '@lib/inputs/react-datepicker';
import { DATE_FORMAT, formatDateToMMDDYYYY } from '@utils/dates/dateMethods';
import { changeFieldValue } from '../common/enrollmentUtils';
import { IEnrollee, IEnrollment } from '../common/types';
import { EnrollmentContext } from '../context/MemberMyEnrollmentProvider';

export const StyledGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr) 1fr;
    grid-template-rows: auto;
    gap: 16px 8px;
    & > div:nth-child(n + 4) {
        grid-column: span 1;
    }
    padding: 1rem;
`;

export const StyledHeader = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
    margin-top: 2rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid ${({ theme }) => theme.gray8};
    font-weight: bold;
`;

const StyledField = styled.div`
    gap: 20px;

    > div {
        display: flex;
        flex-direction: column;
        align-items: start;
        margin: 0;
    }

    .gi-input {
        text-align: left;
    }
    .gi-input__label {
        padding: 0;
    }

    input,
    select {
        width: 80%;
    }

    .react-datepicker-wrapper {
        width: 80%;
    }

    .react-datepicker__input-container {
        input {
            width: 100%;
        }
    }
`;

interface IEditEnrolleeDetailsProps {
    enrolleeDataDtoList: IEnrollee[];
    enrollment: IEnrollment;
}

const EditEnrolleeDetails: React.FC<IEditEnrolleeDetailsProps> = ({ enrolleeDataDtoList, enrollment }) => {
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { iseEnrolleeNameEditable, isSelfEnrollment, isEnrolleeGenederEditable } = usePermissions();
    const { userPermissions } = permissions || {};
    const { enableContinueButton, enrollmentPremiumFormContext, selectedEnrollment } = useContext(EnrollmentContext);
    const {
        watch,
        setValue,
        formState: { errors },
    } = enrollmentPremiumFormContext;
    const values = watch();

    const onUpdateFormFields = (index, field, value) => {
        changeFieldValue(setValue, `enrolleeDataDtoList.${index}.${field}`, value);
        enableContinueButton();
    };

    const onUpdateDateFields = (index, field, value) => {
        const date = formatDateToMMDDYYYY(value);
        changeFieldValue(setValue, `enrolleeDataDtoList.${index}.${field}`, date ? date : '');
        enableContinueButton();
    };

    return (
        <>
            {values?.enrolleeDataDtoList?.map((enrollee, index) => (
                <div className="enrollee-editable-repeat" key={enrollee.memberId}>
                    <StyledHeader>
                        {enrollee.relationshipToSubscriber} - {enrollee.memberId}
                    </StyledHeader>

                    <StyledGrid>
                        <StyledField>
                            <Input
                                label={{
                                    labelText: 'member.enrollment_list.premiumhistory.enrollee.firstName',
                                    labelClasses: [''],
                                    wrapperClasses: [''],
                                    isRequired: true,
                                }}
                                input={{
                                    id: 'firstName',
                                    isInputRequired: true,
                                    type: 'text',
                                    value: enrollee.firstName,
                                    disabled: iseEnrolleeNameEditable(userPermissions, enrollee),
                                }}
                                actions={{
                                    handleFieldChange: value => onUpdateFormFields(index, 'firstName', value),
                                }}
                            />
                            <ValidationErrorMessage
                                isVisible={!!errors.enrolleeDataDtoList?.[index]?.firstName}
                                messageText={errors?.enrolleeDataDtoList?.[index]?.firstName?.message}
                            />
                        </StyledField>
                        <StyledField>
                            <Input
                                label={{
                                    labelText: 'member.enrollment_list.premiumhistory.enrollee.middleName',
                                    labelClasses: [''],
                                    wrapperClasses: [''],
                                    isRequired: false,
                                }}
                                input={{
                                    id: 'middleName',
                                    isInputRequired: true,
                                    type: 'text',
                                    value: enrollee.middleName,
                                    disabled: iseEnrolleeNameEditable(userPermissions, enrollee),
                                }}
                                actions={{
                                    handleFieldChange: value => onUpdateFormFields(index, 'middleName', value),
                                }}
                            />
                            <ValidationErrorMessage
                                isVisible={!!errors.enrolleeDataDtoList?.[index]?.middleName}
                                messageText={errors?.enrolleeDataDtoList?.[index]?.middleName?.message}
                            />
                        </StyledField>
                        <StyledField>
                            <Input
                                label={{
                                    labelText: 'member.enrollment_list.premiumhistory.enrollee.lastName',
                                    labelClasses: [''],
                                    wrapperClasses: [''],
                                    isRequired: true,
                                }}
                                input={{
                                    id: 'lastName',
                                    isInputRequired: true,
                                    type: 'text',
                                    value: enrollee.lastName,
                                    disabled: iseEnrolleeNameEditable(userPermissions, enrollee),
                                }}
                                actions={{
                                    handleFieldChange: value => onUpdateFormFields(index, 'lastName', value),
                                }}
                            />
                            <ValidationErrorMessage
                                isVisible={!!errors.enrolleeDataDtoList?.[index]?.lastName}
                                messageText={errors?.enrolleeDataDtoList?.[index]?.lastName?.message}
                            />
                        </StyledField>
                        <div className=""></div>
                        <StyledField>
                            <Dropdown
                                label={{
                                    text: (
                                        <FormattedMessage
                                            id={'member.enrollment_list.premiumhistory.enrollee.gender'}
                                        />
                                    ),
                                    labelClass: [''],
                                }}
                                inputData={{
                                    inputClasses: [''],
                                    options: GENDER_OPTIONS,
                                    type: INPUT_TYPES.DROPDOWN,
                                    value: enrollee.genderLookupLabel,
                                    isInputRequired: true,
                                    disabled: isEnrolleeGenederEditable(userPermissions, enrollee, enrollment),
                                }}
                                inputActions={{
                                    handleFieldChange: value => onUpdateFormFields(index, 'genderLookupLabel', value),
                                }}
                            />
                        </StyledField>
                        <StyledField>
                            <Input
                                label={{
                                    labelText: 'member.enrollment_list.premiumhistory.enrollee.ssn',
                                    labelClasses: [''],
                                    wrapperClasses: [''],
                                    isRequired: false,
                                }}
                                input={{
                                    id: 'ssn',
                                    isInputRequired: false,
                                    type: 'text',
                                    value: enrollee.taxIdentificationNumber,
                                    disabled: true,
                                }}
                                actions={{
                                    handleFieldChange: value => console.log('ssn', value),
                                }}
                            />
                        </StyledField>

                        <StyledField>
                            <ReactDatepicker
                                labelText="member.enrollment_list.premiumhistory.enrollee.benefitStartDate"
                                wrapperClasses={['date-picker']}
                                isRequired
                                id="enrolleeEffectiveStartDateUI"
                                type="text"
                                pattern={DATE_FORMAT}
                                disabled={
                                    !userPermissions?.memberEnrlmntDpndtStrtEndDtEditable || isSelfEnrollment(enrollee)
                                }
                                value={
                                    enrollee?.enrolleeEffectiveStartDateUI
                                        ? new Date(enrollee.enrolleeEffectiveStartDateUI)
                                        : null
                                }
                                withMask
                                onDateChange={value => onUpdateDateFields(index, 'enrolleeEffectiveStartDateUI', value)}
                            />
                            <ValidationErrorMessage
                                isVisible={!!errors.enrolleeDataDtoList?.[index]?.enrolleeEffectiveStartDateUI}
                                messageText={
                                    errors?.enrolleeDataDtoList?.[index]?.enrolleeEffectiveStartDateUI?.message
                                }
                            />
                        </StyledField>
                        <StyledField>
                            <ReactDatepicker
                                labelText="member.enrollment_list.premiumhistory.enrollee.benefitEndDate"
                                wrapperClasses={['date-picker']}
                                isRequired
                                id="enrolleeEffectiveEndDateUI"
                                type="text"
                                pattern={DATE_FORMAT}
                                disabled={
                                    !userPermissions?.memberEnrlmntDpndtStrtEndDtEditable || isSelfEnrollment(enrollee)
                                }
                                value={
                                    enrollee?.enrolleeEffectiveEndDateUI
                                        ? new Date(enrollee.enrolleeEffectiveEndDateUI)
                                        : null
                                }
                                withMask
                                onDateChange={value => onUpdateDateFields(index, 'enrolleeEffectiveEndDateUI', value)}
                            />
                            <ValidationErrorMessage
                                isVisible={!!errors?.enrolleeDataDtoList?.[index]?.enrolleeEffectiveEndDateUI}
                                messageText={errors?.enrolleeDataDtoList?.[index]?.enrolleeEffectiveEndDateUI?.message}
                            />
                        </StyledField>
                    </StyledGrid>
                </div>
            ))}
        </>
    );
};

export default EditEnrolleeDetails;
